import React, { useContext, useState, useEffect, useRef } from 'react';
import { Field, Form, Formik } from 'formik';
import cloneDeep from 'lodash.clonedeep';

import { SalesforceContext, ecommerceProductCategoryName } from 'src/context/salesforce-context';
import {
  isValidName,
  isValidID,
  isValidMobileNumber,
  hasNoSpecialCharacters,
} from 'src/helpers/validation';
import TealiumEvents from 'src/helpers/tealium-events';
import { phoneNumberMaskSpaced, stripWhitespace, idMask } from 'src/helpers/formatting';
import SpinEdit from 'src/components/spin-edit/spin-edit';
import { FloatingInput } from 'src/components/floating-input/floating-input';
import Modal from 'src/components/modal/modal';
import useDebounce from 'src/hooks/use-debounce';
import { getAddressDetailsFromSearchLocation } from 'src/services/location-management.service';
import MiniAppEvents from 'src/helpers/miniapp-events';
import './delivery-address-modal.scss';
import { OnboardingSummaryContext } from '../../../../../context/onboarding-summary-context';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { isVirtualorInstoreDevice } from 'src/helpers/devices';

export default function DeliveryAddressModal(props) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const {
    merchant,
    deviceDeliveryDetails,
    setDeviceDeliveryDetails,
    uniqueTerminals,
    customerType,
  } = useContext(SalesforceContext);
  const [stagedDeviceQuantitiesForAddressAddition, setStagedDeviceQuantitiesForAddressAddition] =
    useState({});

  const { deviceQuantitiesToDisplay } = useContext(OnboardingSummaryContext);

  const [formState, setFormState] = useState({
    manager_name: '',
    trading_as: '',
    id_number: '',
    phone_work: '',
    phone_cell: '',
    street: '',
    building: '',
    suburb: '',
    city: '',
    region: '',
    zip_code: '',
  });
  const [showAddDeviceQuantityUI, setShowAddDeviceQuantityUI] = useState(true);
  const [startFormFilling, setStartFormFilling] = useState(false);

  const [currentSection, setCurrentSection] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const [newDeviceId, setNewDeviceId] = useState('');

  const topOfPage = useRef('top');

  const [addressesAutocomplete, setAddressesAutocomplete] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const selectedAddressDebounced = useDebounce(selectedAddress, 500);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchAutocomplete = async () => {
      const result = await getAddressDetailsFromSearchLocation(
        selectedAddress,
        5,
        abortController,
        undefined,
        undefined,
      );
      if (result) {
        setAddressesAutocomplete(result);
      }
    };

    if (selectedAddress && hasNoSpecialCharacters(selectedAddress)) {
      fetchAutocomplete();

      return () => {
        abortController.abort();
      };
    }
  }, [selectedAddressDebounced]);

  useEffect(() => {
    setCurrentSection(sections[currentSectionIndex]);
  }, [stagedDeviceQuantitiesForAddressAddition, showAddDeviceQuantityUI]);

  useEffect(() => {
    if (!!currentSection && currentSection.onStepStart) {
      currentSection.onStepStart(currentSection.stepName);
    }
  }, [currentSectionIndex]);

  useEffect(() => {
    if (props.show) {
      setCurrentSection(sections[0]);
      setCurrentSectionIndex(0);
      if (topOfPage?.current) {
        topOfPage.current.scrollIntoView(true);
      }
    }
  }, [props.show]);

  useEffect(() => {
    if (props.deliveryAddressToEdit) {
      const stagedQuantities = Object.keys(props.deliveryAddressToEdit.devices).reduce(
        (accum, deviceId, index) => {
          accum[deviceId] = {
            device_id: deviceId,
            number_of_devices: props.deliveryAddressToEdit.devices[deviceId],
            order: index,
          };
          return accum;
        },
        {},
      );
      setStagedDeviceQuantitiesForAddressAddition(stagedQuantities);
      if (Object.values(stagedQuantities).length > 0) {
        setShowAddDeviceQuantityUI(false);
      }
    }
  }, [props.deliveryAddressToEdit]);

  const getAllSelectedDeviceQuantitiesMap = () => {
    const deliveriesCopy = cloneDeep(merchant?.opportunity?.deliveries);
    return Object.values(deliveriesCopy || {}).reduce((accum, delivery) => {
      accum[delivery.device_id] = {
        ...delivery,
        device_id: delivery.device_id,
        number_of_devices: parseInt(delivery.number_of_devices),
      };
      return accum;
    }, {});
  };

  const getOnlySelectedDeviceQuantitiesWithoutDeliveryAddressMap = (
    ignoreDeviceQuantitiesStagedInThisModalButNotYetSaved = false,
  ) => {
    const deviceQuantitiesStillNeedingDeliveryAddress =
      Object.values(deviceDeliveryDetails).reduce((accum, delivery) => {
        if (delivery !== props.deliveryAddressToEdit) {
          Object.keys(delivery.devices).forEach((terminalId) => {
            if (accum[terminalId]) {
              accum[terminalId].number_of_devices -= delivery.devices[terminalId];
            }
          });
        }
        return accum;
      }, getAllSelectedDeviceQuantitiesMap()) || {};

    if (ignoreDeviceQuantitiesStagedInThisModalButNotYetSaved) {
      return deviceQuantitiesStillNeedingDeliveryAddress;
    }

    const selectedDeviceQuantitiesWithoutDeliveryAddressMap =
      Object.values(stagedDeviceQuantitiesForAddressAddition).reduce((accum, delivery) => {
        if (accum[delivery.device_id]) {
          accum[delivery.device_id].number_of_devices -= delivery.number_of_devices;
        }
        return accum;
      }, deviceQuantitiesStillNeedingDeliveryAddress) || {};
    return selectedDeviceQuantitiesWithoutDeliveryAddressMap;
  };

  const getAvailableDevicesForDropDown = (selectedDeviceIdToInclude = null) => {
    const devices = Object.values(getOnlySelectedDeviceQuantitiesWithoutDeliveryAddressMap())
      .filter(
        (delivery) =>
          (delivery.number_of_devices > 0 &&
            !isVirtualorInstoreDevice(delivery.acquiring_channel) &&
            delivery.product_category !== ecommerceProductCategoryName &&
            !stagedDeviceQuantitiesForAddressAddition[delivery.device_id]) ||
          (selectedDeviceIdToInclude && selectedDeviceIdToInclude === delivery.device_id),
      )
      .map((delivery) => ({
        key: delivery.device_name,
        value: delivery.device_id,
        label: delivery.device_name,
        selected: selectedDeviceIdToInclude && selectedDeviceIdToInclude === delivery.device_id,
      }));
    return devices;
  };

  const getMaxQuantitiesForDevice = (deviceId) => {
    const device = getOnlySelectedDeviceQuantitiesWithoutDeliveryAddressMap(true)[deviceId];
    return device ? device.number_of_devices : 0;
  };

  const getInitialFormValues = () => {
    let terminals = {};

    if (
      stagedDeviceQuantitiesForAddressAddition &&
      Object.keys(stagedDeviceQuantitiesForAddressAddition).length > 0
    ) {
      Object.values(stagedDeviceQuantitiesForAddressAddition).forEach((device) => {
        terminals[`terminal_quantity-${device.device_id}`] = device.number_of_devices;
        terminals[`terminal_name-${device.device_id}`] = uniqueTerminals[device.device_id].name;
      });
    }

    if (props.deliveryAddressToEdit) {
      return {
        ...props.deliveryAddressToEdit,
        ...terminals,
      };
    }

    return {
      ...formState,
      ...terminals,
    };
  };

  const setDeviceIDForStagedDeviceQuantity = (terminalId, stagedDeviceQuantity) => {
    const selectedDeviceQuantitiesCopy = cloneDeep(stagedDeviceQuantitiesForAddressAddition);
    const existingOrder = selectedDeviceQuantitiesCopy[stagedDeviceQuantity.device_id].order;
    delete selectedDeviceQuantitiesCopy[stagedDeviceQuantity.device_id];
    selectedDeviceQuantitiesCopy[terminalId] = {
      device_id: terminalId,
      number_of_devices: 1,
      order: existingOrder,
    };

    setStagedDeviceQuantitiesForAddressAddition(selectedDeviceQuantitiesCopy);
  };

  const setQuantityForStagedDeviceQuantity = (quantity, stagedDeviceQuantity) => {
    setStagedDeviceQuantitiesForAddressAddition({
      ...stagedDeviceQuantitiesForAddressAddition,
      [stagedDeviceQuantity.device_id]: {
        device_id: stagedDeviceQuantity.device_id,
        number_of_devices: parseInt(quantity),
        order: stagedDeviceQuantity.order,
      },
    });
  };

  const addNewStagedDeviceQuantity = (deviceId, quantity) => {
    setStagedDeviceQuantitiesForAddressAddition({
      ...stagedDeviceQuantitiesForAddressAddition,
      [deviceId]: {
        device_id: deviceId,
        number_of_devices: parseInt(quantity),
        order: Object.values(stagedDeviceQuantitiesForAddressAddition).length,
      },
    });
    setNewDeviceId('');
    setShowAddDeviceQuantityUI(false);
  };

  const removeStagedDeviceQuantity = (deviceId) => {
    let stagedDeviceQuantitiesForAddressAdditionCopy = {
      ...stagedDeviceQuantitiesForAddressAddition,
    };
    delete stagedDeviceQuantitiesForAddressAdditionCopy[deviceId];
    setStagedDeviceQuantitiesForAddressAddition(stagedDeviceQuantitiesForAddressAdditionCopy);
  };

  const showAddStagedDeviceQuantityButton = () =>
    !showAddDeviceQuantityUI && getAvailableDevicesForDropDown().length > 0;

  const provinces = [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape',
  ];

  const textTable = {
    dealer: {
      sectionTitle: 'Which devices does your customer want to send to this address?',
    },
    agent: {
      sectionTitle: 'Which devices does your customer want to send to this address?',
    },
    normal: {
      sectionTitle: 'Which devices do you want to send to this address?',
    },
    pos: {
      sectionTitle: 'Which devices do you want to send to this address?',
    },
  };

  const addressSection = ({ setValues, addressesAutocomplete, sendFormStartEvent, values }) => (
    <>
      <div className="address-fields-container">
        <Field
          name="street"
          type="text"
          placeholder="Street address"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          scrollbarSpacing={true}
          items={addressesAutocomplete}
          getItemDisplayValue={(item) => item.addressName}
          onInputValueChange={(newValue) => setSelectedAddress(newValue)}
          onItemSelect={(item) => {
            if (item) {
              TealiumEvents.toolAddressUseAutofillConfirmDeliveryAddress(customerType);
              const selectedItem = item || {};
              const newValues = {
                ...values,
                ...selectedItem.address,
                street: selectedItem.address?.streetNumber
                  ? `${selectedItem.address?.streetNumber} ${selectedItem.address?.street}`
                  : selectedItem.address?.street,
                building: !!selectedItem.address?.building
                  ? `${selectedItem.address?.unit || ''} ${selectedItem.address?.building || ''}`
                  : '',
                suburb: selectedItem.address?.suburb || '',
                city: selectedItem.address?.city || '',
                region: selectedItem.address?.province || '',
                zip_code: selectedItem.address?.postalCode || '',
              };
              setValues(newValues);
              return newValues;
            }
          }}
          fieldType="autocomplete"
          automationIdPrefix="Onboarding_Confirm_Your_Application_Address_Modal"
          styleIconOpen="icon-location-icon"
          subtext="Eg. 12 Ridge Street"
          onClick={() => sendFormStartEvent()}
        />
        <Field
          name="building"
          type="text"
          placeholder="Complex / Building (Optional)"
          subtext="Complex, building or mall name, unit or shop number"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          fieldType="standard"
          automationid={`${props.automationPageName}_Field_Suburb`}
          scrollbarSpacing={true}
        />
        <Field
          name="suburb"
          placeholder="Suburb"
          type="text"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          fieldType="standard"
          automationid="Onboarding_Confirm_Your_Application_Address_Modal_Field_Suburb"
          scrollbarSpacing={true}
        />
        <Field
          name="city"
          type="text"
          placeholder="City / Town"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          fieldType="standard"
          automationid="Onboarding_Confirm_Your_Application_Address_Modal_Field_City"
          scrollbarSpacing={true}
        />
        <Field
          name="region"
          type="text"
          placeholder="Province"
          items={provinces}
          getItemDisplayValue={(item) => item}
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          fieldType="select"
          automationIdPrefix="Onboarding_Confirm_Your_Application_Address_Modal"
          scrollbarSpacing={true}
        />
        <Field
          maxLength="4"
          name="zip_code"
          placeholder="Postal code"
          type="text"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          fieldType="standard"
          className="bottom-field"
          automationid="Onboarding_Confirm_Your_Application_Address_Modal_Field_Postal_Code"
          scrollbarSpacing={true}
        />
      </div>
      <div className="modal-subtext has-text-centered">
        Please note: Deliveries can only be made to a business or operating address, deliveries are
        made Monday to Friday between 8am and 5pm.
      </div>
    </>
  );

  const receiverDetails = ({ sendFormStartEvent }) => (
    <div className="receiver-fields-container">
      <Field
        name="manager_name"
        type="text"
        placeholder="Recipient full name"
        component={MiniAppFocusWrapper}
        innerComponent={FloatingInput}
        fieldType="standard"
        className="top-field"
        automationid="Onboarding_Confirm_Your_Application_Field_Recipient_Full_Names"
        autoFocus
        onFocus={() => sendFormStartEvent()}
        scrollbarSpacing={true}
      />
      <Field
        name="id_number"
        type="tel"
        placeholder="Recipient ID number"
        component={MiniAppFocusWrapper}
        innerComponent={FloatingInput}
        mask={idMask}
        guide={!inMiniProgram}
        fieldType="masked"
        automationid="Onboarding_Confirm_Your_Application_Field_Id_Number"
        scrollbarSpacing={true}
      />
      <Field
        mask={phoneNumberMaskSpaced}
        id="phone_cell"
        name="phone_cell"
        automationid="Onboarding_Confirm_Your_Application_Field_Phone_Cell"
        placeholder="Recipient cell number"
        className="bottom-field"
        type="tel"
        guide={!inMiniProgram}
        fieldType="masked"
        component={MiniAppFocusWrapper}
        innerComponent={FloatingInput}
        scrollbarSpacing={true}
      />
      <Field
        mask={phoneNumberMaskSpaced}
        id="phone_work"
        name="phone_work"
        automationid="Onboarding_Confirm_Your_Application_Field_Phone_Work"
        placeholder="Recipient work number"
        className="bottom-field"
        type="tel"
        guide={!inMiniProgram}
        fieldType="masked"
        component={MiniAppFocusWrapper}
        innerComponent={FloatingInput}
        scrollbarSpacing={true}
      />
      <Field
        name="trading_as"
        type="text"
        placeholder="Trading name"
        component={MiniAppFocusWrapper}
        innerComponent={FloatingInput}
        fieldType="standard"
        automationid="Onboarding_Confirm_Your_Application_Field_Trading_As"
        className="bottom-field"
        scrollbarSpacing={true}
      />
    </div>
  );

  const deliveryAllocation = ({ values, sendFormStartEvent }) => {
    return (
      <>
        <div className="container is-fluid has-padding-bottom-5">
          <div className="modal-intro has-text-centered has-padding-top-0">
            {`${values.street}, ${
              !!values.building && values.building.length > 1 ? values.building + ', ' : ''
            }${values.suburb}, ${values.city}, ${values.zip_code}`}
          </div>
        </div>
        {Object.values(stagedDeviceQuantitiesForAddressAddition)
          .sort((a, b) => a.order - b.order)
          .map((stagedDeviceQuantity, index) => {
            return (
              <div className="container has-divider has-padding-bottom-6" key={index}>
                <div className="columns is-gapless is-mobile is-multiline">
                  <div className="column has-margin-top-6 is-10-mobile">
                    <Field
                      name={`terminal_name-${stagedDeviceQuantity.device_id}`}
                      type="text"
                      placeholder="Select a device"
                      items={getAvailableDevicesForDropDown(
                        stagedDeviceQuantity && stagedDeviceQuantity.device_id,
                      )}
                      getItemDisplayValue={(item) => item.label}
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInput}
                      fieldType="select"
                      automationIdPrefix="Onboarding_Confirm_Your_Application_Address_Modal_Device_New"
                      onItemSelect={(item) =>
                        setDeviceIDForStagedDeviceQuantity(item.value, stagedDeviceQuantity)
                      }
                      onClick={() => sendFormStartEvent()}
                    />
                  </div>
                  <div className="column is-2 remove-cross is-hidden-tablet">
                    <span
                      className="icon-cross"
                      onClick={() => removeStagedDeviceQuantity(stagedDeviceQuantity.device_id)}
                      automationid={`Onboarding_Confirm_Your_Application_Span_Remove_Combination_${index}`}
                    ></span>
                  </div>
                  <div className="column spin-edit-alignment">
                    <Field
                      name={`terminal_quantity-${stagedDeviceQuantity.device_id}`}
                      automationid={`Onboarding_Confirm_Your_Application_Field_Quantity_Combination_${stagedDeviceQuantity.device_id}`}
                      component={SpinEdit}
                      min={1}
                      max={getMaxQuantitiesForDevice(stagedDeviceQuantity.device_id)}
                      className="light"
                      onModified={(newNumber) => {
                        setQuantityForStagedDeviceQuantity(newNumber, stagedDeviceQuantity);
                      }}
                    />
                  </div>
                  <div className="column is-1 remove-cross is-hidden-mobile">
                    <span
                      className="icon-cross"
                      onClick={() => removeStagedDeviceQuantity(stagedDeviceQuantity.device_id)}
                      automationid={`Onboarding_Confirm_Your_Application_Span_Remove_Combination_${index}`}
                    ></span>
                  </div>
                </div>
              </div>
            );
          })}
        {showAddDeviceQuantityUI && (
          <div>
            <div className="columns is-gapless has-margin-top-5">
              <div className="column has-margin-top-6">
                <Field
                  name="terminal-new"
                  type="text"
                  placeholder="Select a device"
                  items={getAvailableDevicesForDropDown()}
                  getItemDisplayValue={(item) => item.label}
                  component={MiniAppFocusWrapper}
                  innerComponent={FloatingInput}
                  fieldType="select"
                  automationIdPrefix="Onboarding_Confirm_Your_Application_Address_Modal_Device_New"
                  onItemSelect={(item) => addNewStagedDeviceQuantity(item.value, 1)}
                  onClick={() => sendFormStartEvent()}
                />
              </div>
              <div className="column spin-edit-alignment">
                <Field
                  name={`spin-edit-${newDeviceId}`}
                  automationid={`Onboarding_Confirm_Your_Application_Field_Quantity_Combination_${newDeviceId}`}
                  component={SpinEdit}
                  className="light"
                  min={1}
                  max={getMaxQuantitiesForDevice(newDeviceId)}
                  value={1}
                  disabled={true}
                />
              </div>
              <div className="column is-1"></div>
            </div>
          </div>
        )}

        {showAddStagedDeviceQuantityButton() && (
          <div className="add-staged-address-button has-text-centered">
            <div
              className="icon-plus "
              onClick={() => {
                setShowAddDeviceQuantityUI(true);
              }}
              automationid={`Onboarding_Confirm_Your_Application_Div_Add_New_Staged_Quantity_Combination`}
            />
            <div className="tiny-sub-heading">Add device</div>
          </div>
        )}
      </>
    );
  };

  const validateAddressSection = (values) => {
    const errors = {};

    if (!hasNoSpecialCharacters(values?.street) || !values?.street) {
      errors.street = 'The street address entered is invalid';
    }

    if (!!values?.building && !hasNoSpecialCharacters(values?.building)) {
      errors.building = 'The building entered is invalid';
    }

    if (!hasNoSpecialCharacters(values?.suburb) || !values?.suburb) {
      errors.suburb = 'The suburb entered is invalid';
    }

    if (!hasNoSpecialCharacters(values?.city) || !values?.city) {
      errors.city = 'The city/town entered is invalid';
    }

    if (!values?.region) {
      errors.region = 'The province entered is invalid';
    }

    if (!values?.zip_code || !/^[0-9]*$/.test(values?.zip_code) || values?.zip_code.length !== 4) {
      errors.zip_code = 'The postal code entered is invalid';
    }

    return errors;
  };

  const validateReceiverDetails = (values) => {
    const errors = {};

    if (!isValidName(values.manager_name) || !values?.manager_name) {
      errors.manager_name = 'The recipient full names entered are invalid';
    }

    if (!isValidID(stripWhitespace(values.id_number)) || !values?.id_number) {
      errors.id_number = 'The recipient ID number entered is invalid';
    }

    if (!isValidMobileNumber(stripWhitespace(values.phone_cell)) || !values?.phone_cell) {
      errors.phone_cell = 'The recipient cell number entered is invalid';
    }

    if (!isValidMobileNumber(stripWhitespace(values.phone_work)) || !values?.phone_work) {
      errors.phone_work = 'The recipient work number entered is invalid';
    }

    if (!hasNoSpecialCharacters(values.trading_as) || !values?.trading_as) {
      errors.trading_as = 'The trading name entered is invalid';
    }

    return errors;
  };

  const validateDeliveryAllocation = (values) => {
    const errors = {};

    if (Object.values(stagedDeviceQuantitiesForAddressAddition).length == 0) {
      errors.devices = values.address ? 'No devices selected' : null;
    }

    return errors;
  };

  const sections = [
    {
      step: 1,
      stepLabel: 'Delivery address',
      stepName: 'delivery address',
      sectionTitle: "Let's add a delivery address",
      form: addressSection,
      validation: validateAddressSection,
      onStepStart: (stepName) =>
        TealiumEvents.confirmAddAddressJourneyStepFormStart(
          stepName,
          customerType,
          merchant.account.business_type,
        ),
      onStepEnd: (stepName, additionalFields) =>
        TealiumEvents.confirmAddAddressJourneyStepFormEnd(
          stepName,
          customerType,
          false,
          additionalFields,
        ),
    },
    {
      step: 2,
      stepLabel: 'Recipient details',
      stepName: 'recipient details',
      sectionTitle: 'Now, who will be receiving the delivery?',
      form: receiverDetails,
      validation: validateReceiverDetails,
      onStepStart: (stepName) =>
        TealiumEvents.confirmAddAddressJourneyStepFormStart(
          stepName,
          customerType,
          merchant.account.business_type,
        ),
      onStepEnd: (stepName, additionalFields) =>
        TealiumEvents.confirmAddAddressJourneyStepFormEnd(
          stepName,
          customerType,
          false,
          additionalFields,
        ),
    },
    {
      step: 3,
      stepLabel: 'Device allocation',
      stepName: 'device allocation',
      sectionTitle: textTable[customerType].sectionTitle,
      form: deliveryAllocation,
      validation: validateDeliveryAllocation,
      onStepStart: (stepName) =>
        TealiumEvents.confirmAddAddressJourneyStepFormStart(
          stepName,
          customerType,
          merchant.account.business_type,
        ),
      onStepEnd: (stepName, additionalFields) =>
        TealiumEvents.confirmAddAddressJourneyStepFormEnd(
          stepName,
          customerType,
          true,
          additionalFields,
        ),
    },
  ];

  const nextSection = (values) => {
    if (currentSectionIndex < sections.length) {
      if (currentSection.onStepEnd) {
        currentSection.onStepEnd(currentSection.stepName);
      }
      setFormState(values);
      setStartFormFilling(false);
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentSection(sections[currentSectionIndex + 1]);
    }
  };

  const prevSection = () => {
    if (currentSectionIndex > 0) {
      setStartFormFilling(false);
      setCurrentSectionIndex(currentSectionIndex - 1);
      setCurrentSection(sections[currentSectionIndex - 1]);
    }
  };

  return (
    <Formik
      initialValues={getInitialFormValues()}
      validateOnChange={true}
      validateOnBlur={false}
      enableReinitialize={true}
      validate={validateModalForm}
    >
      {({ values, resetForm, setValues }) => {
        return (
          <Form>
            {!!currentSection && (
              <Modal
                modalStyle="DeliveryAddressModal"
                backdropStyle="backdrop"
                bodyStyle="modal-body-style"
                cardStyle="delivery-address-card"
                show={props.show}
                modalProps={{ className: 'is-full-height' }}
                header={() => (
                  <div className="container modal-header has-text-centered has-padding-top-3 is-fluid">
                    <div ref={topOfPage} className="delivery-address-steps">
                      {sections.map((section, index) => (
                        <div className="is-hidden-mobile step" key={`step-${index}`}>
                          <div
                            className={`progress-step ${
                              currentSectionIndex > index
                                ? 'completed'
                                : currentSectionIndex < index
                                ? 'outstanding'
                                : ''
                            }`}
                          >
                            {currentSectionIndex > index && (
                              <div className="icon-check-mark icon" />
                            )}
                            {currentSectionIndex < index && (
                              <div className="step-number">{section.step}</div>
                            )}
                          </div>
                          <div
                            className={`step-label ${
                              currentSectionIndex === index ? 'active' : ''
                            }`}
                          >
                            {section.stepLabel}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className="close-cross icon-cross"
                      onClick={() => {
                        resetFormAll(values, resetForm);
                        TealiumEvents.confirmAddAddressExitButtonClick(
                          currentSection.stepName,
                          customerType,
                        );
                        props.onCancel();
                      }}
                    ></div>
                    <h3>{currentSection.sectionTitle}</h3>
                  </div>
                )}
                body={() => (
                  <div className=" is-fullheight">
                    <div className="container delivery-address-container">
                      {currentSection.form({
                        values,
                        setValues,
                        addressesAutocomplete,
                        sendFormStartEvent,
                      })}
                    </div>
                  </div>
                )}
                footer={() => (
                  <span className="container is-fluid">
                    <div className="buttons ">
                      {currentSectionIndex > 0 && (
                        <div className="button-container">
                          <button
                            className=" button is-secondary center-align cancel-button"
                            type="button"
                            onClick={prevSection}
                            automationid="Onboarding_Delivery_Address_Modal_Back"
                          >
                            Back
                          </button>
                        </div>
                      )}
                      {currentSectionIndex < sections.length - 1 ? (
                        <div className="button-container">
                          <button
                            className="button is-primary center-align cancel-button"
                            type="button"
                            onClick={() => nextSection(values)}
                            disabled={Object.keys(validateModalForm(values)).length !== 0}
                            automationid="Onboarding_Delivery_Address_Modal_Next"
                          >
                            Next
                          </button>
                        </div>
                      ) : (
                        <div className="button-container">
                          <button
                            className="button is-primary center-align cancel-button"
                            type="button"
                            onClick={() => submitForm(values, resetForm)}
                            disabled={Object.keys(validateModalForm(values)).length !== 0}
                            automationid="Onboarding_Delivery_Address_Modal_Submit"
                          >
                            Done
                          </button>
                        </div>
                      )}
                    </div>
                  </span>
                )}
                footerStyle="center-items fixed-footer"
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );

  function sendFormStartEvent() {
    if (!startFormFilling) {
      setStartFormFilling(true);
      if (currentSection.onStepStart) {
        currentSection.onStepStart(currentSection.stepName);
      }
    }
  }

  async function resetFormAll(values, resetForm) {
    setStartFormFilling(false);
    setStagedDeviceQuantitiesForAddressAddition({});
    setShowAddDeviceQuantityUI(true);
    setNewDeviceId('');
    resetForm();
    setCurrentSectionIndex(0);
    setFormState({
      manager_name: '',
      trading_as: '',
      id_number: '',
      phone_work: '',
      phone_cell: '',
      street: '',
      building: '',
      suburb: '',
      city: '',
      region: '',
      zip_code: '',
    });
  }

  async function submitForm(values, resetForm) {
    let deliveriesWithAddressCopy = cloneDeep(deviceDeliveryDetails);
    const newDelivery = {
      ...values,
      devices: Object.values(stagedDeviceQuantitiesForAddressAddition).reduce((accum, element) => {
        accum[element.device_id] = element.number_of_devices;
        return accum;
      }, {}),
    };

    if (currentSection.onStepEnd) {
      currentSection.onStepEnd(currentSection.stepName, {
        number_of_devices: Object.values(newDelivery.devices).reduce(
          (accum, value) => accum + value,
        ),
        smme_business_addresses:
          deviceQuantitiesToDisplay().length + (!props.modalInitialDeviceQuantity ? 1 : 0),
      });
    }

    if (props.deliveryAddressIsNonDefault) {
      const index = deviceDeliveryDetails.indexOf(props.deliveryAddressToEdit);
      deliveriesWithAddressCopy[index] = newDelivery;
    } else {
      let newDelivery = {
        ...values,
        devices: Object.values(stagedDeviceQuantitiesForAddressAddition).reduce(
          (accum, element) => {
            accum[element.device_id] = element.number_of_devices;
            return accum;
          },
          {},
        ),
      };
      newDelivery.address_type = 'operational';
      deliveriesWithAddressCopy.push(newDelivery);
    }
    setDeviceDeliveryDetails(deliveriesWithAddressCopy);

    await resetFormAll(values, resetForm);
    props.onSubmit();
  }

  function validateModalForm(values) {
    const errors = currentSection.validation(values);

    return errors;
  }
}
